// @ts-nocheck
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Autocomplete, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import { getErrorHelperText } from '../../../../utils/Utils';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../const/CommonConst';
import useAttribute from '../../../../hooks/ModelInventory/useAttribute';
import {
  composeDraftBlurHandler,
  scheduleDraftPersistOnBlur,
} from '../../../../hooks/useDraftAutoSave.jsx';
import { DRAFT_INPUT_SX } from './TextFieldComponent';

const getUserSelectOptionLabel = (option) => option?.username || option?.user;

/**
 * User Select field component for selecting users
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string|number} props.value - Current selected username or user_id
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - The attribute being edited
 * @param {Array} props.usersList - List of available users
 * @returns {React.ReactElement} The UserSelectFieldComponent
 */
const UserSelectFieldComponent = ({
  value,
  onChange,
  usersList = EMPTY_ARRAY,
  label,
  titleLabel,
  attribute,
  entityDetailsFormikForm,
  isLoadingConstraints = false,
  required = false,
  formData = EMPTY_OBJECT,
  entityData = EMPTY_OBJECT,
  loader,
  isMultipleEditMode = false,
  attributeValues = EMPTY_OBJECT,
  hasDraftChip = false,
  onDraftBlur,
}) => {
  const { getUsersAttributesData } = useAttribute();
  const [apiUsersList, setApiUsersList] = useState(EMPTY_ARRAY);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const skipConstraintLogic = false;

  const { tenant } = useSelector((state) => state);
  const { pluginConfig } = tenant;
  const isHDFCUAMEnabled = useMemo(
    () => !!pluginConfig?.HDFCUAM,
    [pluginConfig]
  );
  const errorHelperText = useMemo(
    () =>
      getErrorHelperText(
        entityDetailsFormikForm?.errors?.[attribute?.id],
        entityDetailsFormikForm?.touched?.[attribute?.id]
      ),
    [
      entityDetailsFormikForm?.errors,
      entityDetailsFormikForm?.touched,
      attribute?.id,
    ]
  );

  const hasError = useMemo(() => Boolean(errorHelperText), [errorHelperText]);

  const isTeamUserOnlyAttribute = useMemo(() => {
    return (
      attribute?.data_type === 'User' &&
      ['assignedUser'].includes(attribute?.name)
    );
  }, [attribute?.data_type, attribute?.name]);

  const filteredUsersList = useMemo(() => {
    // Use API users list when constraint logic is not skipped and API data is available
    let currentUsersList =
      !skipConstraintLogic &&
      apiUsersList?.length > 0 &&
      !isTeamUserOnlyAttribute
        ? apiUsersList
        : usersList;
    const uniqueUsernames = new Set();
    return currentUsersList.filter((user) => {
      const userName = user?.username || user?.user;
      if (userName && !uniqueUsernames.has(userName)) {
        uniqueUsernames.add(userName);
        return true;
      }
      return false;
    });
  }, [
    usersList,
    isHDFCUAMEnabled,
    attribute?.name,
    apiUsersList,
    skipConstraintLogic,
    isTeamUserOnlyAttribute,
  ]);

  const fetchUsersData = useCallback(() => {
    if (isLoading || skipConstraintLogic || !entityData?.entity_type) {
      return;
    }
    setIsLoading(true);
    const payload = {
      source_attribute_id: Number.parseInt(attribute?.id),
      form_data: formData,
      ...(entityData?.draft_entity_key
        ? { draft_entity_key: entityData?.draft_entity_key }
        : { entity_id: entityData?.entity_id }),
      entity_type: entityData?.entity_type,
      skip_constraint_logic: skipConstraintLogic,
    };

    getUsersAttributesData(payload, loader)
      .then((res) => {
        if (res?.data) {
          setApiUsersList(res.data);
        } else {
          setApiUsersList(EMPTY_ARRAY);
        }
      })
      .catch(() => {
        setApiUsersList(EMPTY_ARRAY);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    attribute?.id,
    formData,
    getUsersAttributesData,
    isLoading,
    entityData,
    loader,
    skipConstraintLogic,
  ]);

  const handleDropdownOpen = useCallback(() => {
    setIsDropdownOpen(true);
    fetchUsersData();
  }, [fetchUsersData, setIsDropdownOpen]);

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false);
  }, [setIsDropdownOpen]);

  const isDerived = useMemo(
    () => attribute?.is_derived,
    [attribute?.is_derived]
  );

  /**
   * Handles user selection changes
   * @function
   * @param {Event} event - The change event
   * @param {Object} user - The selected user
   * @returns {void}
   */
  const handleChange = useCallback(
    (_event, user) => {
      const selectedUsername = user ? user?.username || user?.user : null;
      onChange(selectedUsername);
      scheduleDraftPersistOnBlur(onDraftBlur);
    },
    [onChange, onDraftBlur]
  );

  // Find the user object that matches the current value (whether it's a username or user_id), This is because we are getting username in the get attribute data api and we are sending the user id to the backend. So when we change the user locally we send the user_id and in initial load we get the username in value.
  const selectedUser = useMemo(() => {
    if (!value) return null;

    // we will always receive username from backend, and maintain username in formik form and also in draft
    // but we will transform that username into id while sending to BE
    const found = filteredUsersList.find((user) => {
      const userName = user?.username || user?.user;
      return userName === value || user?.user_id === value;
    });

    if (found) return found;

    if (typeof value === 'object') {
      const userName = value?.username || value?.user;
      if (userName) {
        return {
          ...value,
          username: userName,
          user_id: value?.user_id,
        };
      }
      return value;
    }

    return {
      username: String(value),
      user_id: value,
    };
  }, [filteredUsersList, value]);

  const handleBlur = useCallback(
    (event) => {
      composeDraftBlurHandler(
        () =>
          entityDetailsFormikForm?.setFieldTouched(attribute?.id, true, true),
        onDraftBlur
      )(event);
    },
    [entityDetailsFormikForm, attribute?.id, onDraftBlur]
  );

  const renderUserSelectTextField = useCallback(
    (params) => (
      <TextField
        {...params}
        label={label}
        title={titleLabel}
        required={required}
        error={hasError}
        helperText={errorHelperText}
        sx={hasDraftChip ? DRAFT_INPUT_SX : undefined}
        slotProps={{
          ...params?.slotProps,
          input: {
            ...params?.slotProps?.input,
            placeholder: 'Select user',
          },

          inputLabel: {
            ...params?.slotProps?.inputLabel,
            shrink: true,
          },
        }}
      />
    ),
    [label, hasError, errorHelperText, hasDraftChip]
  );

  // Re-fetch users and validate selection when constraints finish loading, this is added because when constraints are applied, the available options might change based on the constraint logic and we need to make sure that the selected value is still valid with the new options, if not we need to clear the selection.
  const prevIsLoadingConstraintsRef = useRef(isLoadingConstraints);
  useEffect(() => {
    const wasLoading = prevIsLoadingConstraintsRef.current;
    prevIsLoadingConstraintsRef.current = isLoadingConstraints;

    // When constraints transition from loading → done, re-fetch users
    if (
      wasLoading &&
      !isLoadingConstraints &&
      !skipConstraintLogic &&
      attribute?.id
    ) {
      const payload = {
        source_attribute_id: Number.parseInt(attribute?.id),
        form_data: formData,
        ...(entityData?.draft_entity_key
          ? { draft_entity_key: entityData?.draft_entity_key }
          : { entity_id: entityData?.entity_id }),
        entity_type: entityData?.entity_type,
        skip_constraint_logic: skipConstraintLogic,
      };

      getUsersAttributesData(payload, false)
        .then((res) => {
          if (res?.data) {
            setApiUsersList(res.data);
            // Clear selection if current value is no longer in the new options
            if (value) {
              const isValueValid = res.data.some(
                (user) =>
                  (user?.username || user?.user) === value ||
                  user?.user_id === value
              );
              if (!isValueValid) {
                onChange(null);
              }
            }
          } else {
            setApiUsersList(EMPTY_ARRAY);
            if (value) {
              onChange(null);
            }
          }
        })
        .catch(() => {});
    }
  }, [isLoadingConstraints]);

  // HDFC assignedUser: read-only display in multi-edit; use Formik value (includes draft), not attributeValues
  if (
    attribute?.name === 'assignedUser' &&
    isMultipleEditMode &&
    isHDFCUAMEnabled
  ) {
    const displayLabel =
      selectedUser?.username ??
      selectedUser?.user ??
      (typeof value === 'string' && Number.isNaN(Number(value)) ? value : '') ??
      attributeValues?.[attribute?.id] ??
      '';
    return <Typography variant="body8">{displayLabel}</Typography>;
  }

  return (
    <span
      title={
        isDerived
          ? 'This field is disabled because it is derived'
          : `${attribute?.label}`
      }
    >
      <Autocomplete
        fullWidth
        disabled={isDerived}
        readOnly={isLoadingConstraints}
        label={label}
        title={titleLabel}
        value={selectedUser || null}
        options={isLoading ? EMPTY_ARRAY : filteredUsersList}
        popupIcon={<DropdownArrow />}
        getOptionLabel={getUserSelectOptionLabel}
        renderInput={renderUserSelectTextField}
        onChange={handleChange}
        onBlur={handleBlur}
        onOpen={handleDropdownOpen}
        onClose={handleDropdownClose}
        open={isDropdownOpen}
        loading={isLoading}
        loadingText="Loading users..."
        style={{ pointerEvents: isDerived ? 'none' : 'auto' }}
      />
    </span>
  );
};

UserSelectFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    is_derived: PropTypes.bool.isRequired,
    label: PropTypes.string.isRequired,
    data_type: PropTypes.string,
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    errors: PropTypes.objectOf(PropTypes.string),
    touched: PropTypes.objectOf(PropTypes.bool),
    setFieldTouched: PropTypes.func.isRequired,
  }),
  usersList: PropTypes.arrayOf(
    PropTypes.shape({
      username: PropTypes.string,
      user: PropTypes.string,
      user_id: PropTypes.number,
    })
  ),
  isLoadingConstraints: PropTypes.bool,
  required: PropTypes.bool,
  formData: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  entityData: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  loader: PropTypes.bool,
  isMultipleEditMode: PropTypes.bool,
  attributeValues: PropTypes.shape({}),
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
};

export default UserSelectFieldComponent;
